/**
 * 🧪 AlembicPage — The Alembic brewing hub.
 *
 * Canon: /app/memory/ALEMBIC_BREWING_TREE_CANON_FEB2026.md
 * Owner rule: Water is FREE and unlimited. Never tracked.
 *
 * Layout: recipe cards (inputs → output arrow, brew button) with an
 * ingredient counter strip and owner-only "grant test raws" panel.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const RARITY_BADGE = {
  common:    "bg-slate-700/40 border-slate-500 text-slate-200",
  uncommon:  "bg-emerald-900/40 border-emerald-700 text-emerald-200",
  rare:      "bg-blue-900/40 border-blue-700 text-blue-200",
  legendary: "bg-purple-900/40 border-purple-700 text-purple-200",
};

function IngredientChip({ ing }) {
  return (
    <div
      data-testid={`alembic-ingredient-${ing.key}`}
      className="flex items-center gap-1.5 rounded-full border border-purple-800/40 bg-slate-950/60 px-2.5 py-1 text-xs"
    >
      <span className="text-base">{ing.icon}</span>
      <span className="text-purple-100">{ing.name}</span>
      <span className="rounded bg-black/50 px-1.5 py-0.5 font-mono text-[11px] text-purple-200">
        ×{ing.owned}
      </span>
    </div>
  );
}

function RecipeCard({ recipe, ingMap, onBrew, brewing }) {
  const missing = recipe.missing || [];
  const rarity = recipe.rarity || "common";
  // ✨ Newly-available ribbon — 24 h decay from the transition moment.
  const unlockedAt = recipe.unlocked_at ? new Date(recipe.unlocked_at).getTime() : null;
  const ageMs = unlockedAt ? Date.now() - unlockedAt : Infinity;
  const isNewlyAvailable = recipe.can_brew && unlockedAt && ageMs < 24 * 3600 * 1000;
  // Fade alpha across 24 h: full (1.0) for first hour, then linear taper to 0.35.
  let ribbonAlpha = 0;
  if (isNewlyAvailable) {
    const hours = ageMs / (3600 * 1000);
    ribbonAlpha = hours < 1 ? 1 : Math.max(0.35, 1 - (hours - 1) / 23 * 0.65);
  }
  return (
    <div
      data-testid={`alembic-recipe-${recipe.recipe_id}`}
      className={`relative flex flex-col gap-3 rounded-xl border p-4 transition-colors ${
        recipe.can_brew
          ? "border-emerald-700/50 bg-gradient-to-b from-emerald-950/30 to-slate-950/80 hover:border-emerald-500/60"
          : "border-purple-900/40 bg-gradient-to-b from-purple-950/20 to-slate-950/80"
      }`}
    >
      {isNewlyAvailable && (
        <div
          data-testid={`alembic-recipe-newly-available-${recipe.recipe_id}`}
          style={{ opacity: ribbonAlpha }}
          className="pointer-events-none absolute -right-2 -top-2 rotate-3 rounded-full border border-amber-400/80 bg-gradient-to-r from-amber-500 to-yellow-400 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-black shadow-[0_0_18px_rgba(251,191,36,0.55)] animate-pulse"
        >
          ✨ Newly available
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-purple-300/80">
            Tier {recipe.tier}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-3xl">{recipe.output_icon}</span>
            <span className="text-lg font-bold text-purple-50">{recipe.output_name}</span>
          </div>
        </div>
        <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase ${RARITY_BADGE[rarity] || RARITY_BADGE.common}`}>
          {rarity}
        </span>
      </div>

      {/* Inputs → output visual */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {Object.entries(recipe.inputs).map(([k, qty]) => {
          const ing = ingMap[k] || { name: k, icon: "❓", owned: 0 };
          const have = ing.owned;
          const enough = have >= qty;
          return (
            <span
              key={k}
              className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${
                enough ? "border-emerald-700/50 text-emerald-100" : "border-red-800/50 text-red-200"
              } bg-slate-950/50`}
            >
              <span>{ing.icon}</span>
              <span className="text-xs">{qty}×</span>
              <span className="text-xs">{ing.name}</span>
              <span className="text-[10px] opacity-70">({have})</span>
            </span>
          );
        })}
        {recipe.water && (
          <span className="inline-flex items-center gap-1 rounded border border-sky-800/50 bg-sky-950/30 px-2 py-1 text-xs text-sky-200">
            💧 Water <span className="text-[10px] opacity-70">(free)</span>
          </span>
        )}
        <span className="text-purple-400">→</span>
        <span className="inline-flex items-center gap-1 rounded border border-purple-700/50 bg-purple-950/40 px-2 py-1 text-xs text-purple-100">
          {recipe.output_icon} 1× {recipe.output_name}
        </span>
      </div>

      {/* Buff / risk */}
      <div className="space-y-1 text-[11px]">
        {recipe.buff && (
          <div className="rounded border border-emerald-800/40 bg-emerald-950/20 px-2 py-1">
            <b className="text-emerald-300">🔥 {recipe.buff.name}</b>{" "}
            <span className="text-emerald-100/80">
              ({recipe.buff.duration_turns} turns):{" "}
              {Object.entries(recipe.buff.effects).map(([k, v]) => (
                <span key={k}>+{v}% {k.replace(/_pct$/, "").replace(/_/g, " ")}. </span>
              ))}
            </span>
          </div>
        )}
        {recipe.risk && (
          <div className="rounded border border-red-800/40 bg-red-950/20 px-2 py-1 text-red-200/90">
            <b className="text-red-300">☠️ {recipe.risk.name}</b>:{" "}
            {Object.entries(recipe.risk)
              .filter(([k]) => k.endsWith("_pct") || k.endsWith("pct_max_hp") || k.endsWith("debuff_pct"))
              .map(([k, v]) => (
                <span key={k}>-{v}% {k.replace(/_pct$/, "").replace(/_/g, " ")}. </span>
              ))}
          </div>
        )}
      </div>

      <button
        data-testid={`alembic-brew-${recipe.recipe_id}`}
        onClick={() => onBrew(recipe)}
        disabled={!recipe.can_brew || brewing}
        className={`mt-1 w-full rounded-lg py-2 text-sm font-bold ${
          recipe.can_brew
            ? "border border-emerald-500/70 bg-gradient-to-br from-emerald-700 to-emerald-500 text-white hover:brightness-110"
            : "border border-slate-700 bg-slate-900/50 text-slate-500 cursor-not-allowed"
        }`}
      >
        {recipe.can_brew ? `🧪 Brew ${recipe.output_name}` : `Missing ${missing.map((m) => m.item).join(", ")}`}
      </button>
    </div>
  );
}

const OWNER_GRANT_QUICK = [
  { key: "grapes",       qty: 12, label: "🍇 +12 Grapes"       },
  { key: "yeast",        qty: 5,  label: "🍚 +5 Yeast"         },
  { key: "rice_paddies", qty: 8,  label: "🌾 +8 Rice Paddies"  },
  { key: "live_cobra",   qty: 1,  label: "🐍 +1 Live Cobra"    },
];

export default function AlembicPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brewing, setBrewing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/alembic/state`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alembic unavailable");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const ingMap = useMemo(() => {
    const m = {};
    (state?.ingredients || []).forEach((i) => { m[i.key] = i; });
    return m;
  }, [state]);

  const doBrew = async (recipe) => {
    setBrewing(true);
    try {
      const { data } = await axios.post(`${API}/alembic/brew`, { recipe_id: recipe.recipe_id });
      toast.success(data.message || `Brewed 1× ${recipe.output_name}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brew failed");
    } finally {
      setBrewing(false);
    }
  };

  const ownerGrant = async (ing, qty) => {
    try {
      await axios.post(`${API}/alembic/owner/grant-ingredient`, { ingredient: ing, qty });
      toast.success(`Granted ${qty}× ${ing}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black text-purple-50">
      <Header />
      <div className="mx-auto max-w-6xl px-4 pt-20 pb-24 md:pt-24" data-testid="alembic-page">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-4xl font-bold tracking-tight text-purple-100 sm:text-5xl">
              🧪 The Alembic
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-purple-300/80">
              Brew from the raws you&apos;ve gathered. Everything you distill here is player-crafted and lists cleanly at the Aethelgard Bazaar.
              Water is on the house — the well never runs dry.
            </p>
          </div>
          <a
            href="/bazaar"
            data-testid="alembic-to-bazaar-link"
            className="rounded-full border border-purple-500/50 bg-purple-900/40 px-4 py-2 text-xs font-semibold text-purple-100 hover:bg-purple-800/60"
          >
            🏛️ Sell at the Bazaar →
          </a>
        </div>

        {/* Ingredient strip */}
        <div className="mb-6 rounded-xl border border-purple-900/40 bg-purple-950/20 p-3">
          <div className="mb-2 text-xs uppercase tracking-wider text-purple-300">Your Ingredients</div>
          <div className="flex flex-wrap gap-2" data-testid="alembic-ingredient-strip">
            {(state?.ingredients || []).map((ing) => (
              <IngredientChip key={ing.key} ing={ing} />
            ))}
            <div className="inline-flex items-center gap-1.5 rounded-full border border-sky-800/40 bg-sky-950/30 px-2.5 py-1 text-xs text-sky-200">
              💧 Water <span className="text-[10px] opacity-70">(unlimited)</span>
            </div>
          </div>
        </div>

        {/* Owner test grants */}
        {state?.is_owner && (
          <div className="mb-6 rounded-xl border border-amber-800/40 bg-amber-950/20 p-3" data-testid="alembic-owner-panel">
            <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-amber-200">
              🧑‍🔧 Owner-only test grants
            </div>
            <div className="flex flex-wrap gap-2">
              {OWNER_GRANT_QUICK.map((g) => (
                <button
                  key={g.key}
                  data-testid={`alembic-owner-grant-${g.key}`}
                  onClick={() => ownerGrant(g.key, g.qty)}
                  className="rounded border border-amber-700/50 bg-amber-950/30 px-3 py-1.5 text-xs font-semibold text-amber-100 hover:bg-amber-900/50"
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Recipes — only those you have all ingredients for */}
        {loading ? (
          <div className="py-16 text-center text-purple-300">Loading recipes…</div>
        ) : (() => {
          const brewable = (state?.recipes || []).filter((r) => r.can_brew);
          const lockedCount = (state?.recipes || []).length - brewable.length;
          return brewable.length === 0 ? (
            <div className="rounded-xl border border-purple-800/40 bg-purple-950/30 p-10 text-center" data-testid="alembic-empty">
              <div className="mb-2 text-4xl">🧪</div>
              <div className="text-lg font-semibold text-purple-100">Nothing to brew yet.</div>
              <div className="mt-1 text-sm text-purple-300/80">
                Gather ingredients — a recipe unlocks itself here the second you have enough.
              </div>
              {lockedCount > 0 && (
                <div className="mt-3 text-[10px] uppercase tracking-wider text-purple-400/60">
                  {lockedCount} recipe{lockedCount !== 1 ? "s" : ""} waiting on ingredients
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="alembic-recipes-grid">
                {brewable.map((r) => (
                  <RecipeCard key={r.recipe_id} recipe={r} ingMap={ingMap} onBrew={doBrew} brewing={brewing} />
                ))}
              </div>
              {lockedCount > 0 && (
                <div className="mt-4 text-center text-[11px] text-purple-400/70" data-testid="alembic-locked-hint">
                  🔒 {lockedCount} more recipe{lockedCount !== 1 ? "s" : ""} unlock when you gather the right raws.
                </div>
              )}
            </>
          );
        })()}

        {/* Brew history */}
        {state?.history?.length > 0 && (
          <div className="mt-8">
            <h2 className="mb-2 text-lg font-bold text-purple-100">🧾 Recent Brews</h2>
            <ul className="space-y-1 text-sm" data-testid="alembic-history">
              {state.history.map((h) => (
                <li key={h.brew_id} className="flex items-center justify-between rounded border border-purple-800/40 bg-purple-950/20 px-3 py-2">
                  <span>{h.output_id}</span>
                  <span className="text-[11px] text-purple-300/70">{h.brewed_at?.slice(0, 19).replace("T", " ")}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
